/**
 * @file MapView.tsx
 * @description Interactive Leaflet map component for displaying bikeparks and snowparks
 * @module components/MapView
 */

'use client';

import { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import '@/styles/cluster.css';
import { Bikepark, Snowpark, Pumptrack } from '@/types';
import WeatherWidget from './WeatherWidget';
import FavoriteButton from './FavoriteButton';
import { useTranslations } from 'next-intl';
import { MapPin, Heart, DollarSign, Snowflake, Map as MapIcon, X, FileText, Globe, Circle } from 'lucide-react';

// Create custom cluster icon - area-based visualization
const createClusterCustomIcon = (cluster: any, type: 'bikepark' | 'snowpark' | 'pumptrack' = 'bikepark') => {
  const count = cluster.getChildCount();

  // Determine size based on count - larger areas for more items
  let size = 80;
  let fontSize = 18;
  let labelSize = 9;

  if (count >= 100) {
    size = 140;
    fontSize = 28;
    labelSize = 11;
  } else if (count >= 50) {
    size = 120;
    fontSize = 24;
    labelSize = 10;
  } else if (count >= 20) {
    size = 100;
    fontSize = 22;
    labelSize = 10;
  } else if (count >= 10) {
    size = 90;
    fontSize = 20;
    labelSize = 9;
  }

  // Color based on type
  let bgGradient = 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)';
  let shadowColor = 'rgba(16, 185, 129, 0.4)';

  if (type === 'pumptrack') {
    bgGradient = 'linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.95) 100%)';
    shadowColor = 'rgba(245, 158, 11, 0.4)';
  } else if (type === 'snowpark') {
    bgGradient = 'linear-gradient(135deg, rgba(59, 130, 246, 0.95) 0%, rgba(37, 99, 235, 0.95) 100%)';
    shadowColor = 'rgba(59, 130, 246, 0.4)';
  }

  // Inline styles to override Leaflet defaults completely
  const containerStyle = `
    width: ${size}px;
    height: ${size}px;
    border-radius: 50%;
    background: ${bgGradient};
    border: 3px solid rgba(255, 255, 255, 0.4);
    box-shadow: 0 0 0 4px ${shadowColor}, 0 4px 12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.25s ease;
    box-sizing: border-box;
  `.replace(/\s+/g, ' ').trim();

  const countStyle = `
    font-size: ${fontSize}px;
    font-weight: 800;
    line-height: 1;
    color: #ffffff;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    display: block;
    letter-spacing: -0.5px;
  `.replace(/\s+/g, ' ').trim();

  const labelStyle = `
    font-size: ${labelSize}px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    margin-top: 1px;
    display: block;
    text-transform: lowercase;
  `.replace(/\s+/g, ' ').trim();

  const label = count === 1 ? 'miejsce' : count < 5 ? 'miejsca' : 'miejsc';

  return L.divIcon({
    html: `<div style="${containerStyle}"><span style="${countStyle}">${count}</span><span style="${labelStyle}">${label}</span></div>`,
    className: 'custom-cluster-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Create custom marker icon based on difficulty level
const createCustomMarkerIcon = (levels: string[] = []) => {
  const levelColor = (levels: string[]) => {
    if (!levels || levels.length === 0) return '#60a5fa'; // blue by default
    if (levels.includes('black')) return '#1f2937';
    if (levels.includes('red')) return '#ef4444';
    if (levels.includes('blue')) return '#3b82f6';
    if (levels.includes('green')) return '#22c55e';
    return '#60a5fa';
  };

  const color = levelColor(levels);

  return L.divIcon({
    className: 'custom-map-marker',
    html: `
      <div class="marker-pin" style="--marker-color: ${color};">
        <svg width="36" height="46" viewBox="0 0 36 46" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <filter id="marker-glow-${color.replace('#', '')}" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
              <feOffset dx="0" dy="2" result="offsetblur"/>
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.5"/>
              </feComponentTransfer>
              <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <linearGradient id="marker-gradient-${color.replace('#', '')}" x1="18" y1="0" x2="18" y2="42" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stop-color="${color}" stop-opacity="1"/>
              <stop offset="100%" stop-color="${color}" stop-opacity="0.85"/>
            </linearGradient>
          </defs>
          <path
            d="M18 0C8.059 0 0 8.059 0 18C0 28.941 18 46 18 46C18 46 36 28.941 36 18C36 8.059 27.941 0 18 0Z"
            fill="url(#marker-gradient-${color.replace('#', '')})"
            filter="url(#marker-glow-${color.replace('#', '')})"
            stroke="rgba(255, 255, 255, 0.3)"
            stroke-width="1.5"
          />
          <circle cx="18" cy="18" r="7" fill="white" opacity="0.95"/>
          <circle cx="18" cy="18" r="4" fill="${color}" opacity="0.9"/>
        </svg>
      </div>
    `,
    iconSize: [36, 46],
    iconAnchor: [18, 46],
    popupAnchor: [0, -46],
  });
};

interface MapViewProps {
  parks: (Bikepark | Snowpark | Pumptrack)[];
  center?: [number, number];
  zoom?: number;
  type?: 'bikepark' | 'snowpark' | 'pumptrack';
  selectedPark?: Bikepark | Snowpark | Pumptrack | null;
  hoveredPark?: Bikepark | Snowpark | Pumptrack | null;
}

function MapController({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);

  return null;
}

// ParkMarker component to handle individual marker logic with hooks
interface ParkMarkerProps {
  park: Bikepark | Snowpark | Pumptrack;
  isSelected: boolean;
  isHovered: boolean;
  renderStars: (rating: number) => string;
  parkType: 'bikepark' | 'snowpark' | 'pumptrack';
}

function ParkMarker({ park, isSelected, isHovered, renderStars, parkType }: ParkMarkerProps) {
  const t = useTranslations('common');
  const levels = park.levels || [];
  const icon = createCustomMarkerIcon(levels);
  const markerRef = useRef<L.Marker>(null);

  // Open popup when park is selected
  useEffect(() => {
    if (isSelected && markerRef.current) {
      markerRef.current.openPopup();
    }
  }, [isSelected]);

  // Add hover effect
  useEffect(() => {
    if (!markerRef.current) return;

    const markerElement = markerRef.current.getElement();
    if (!markerElement) return;

    if (isHovered) {
      markerElement.style.filter = 'drop-shadow(0 0 12px rgba(16, 185, 129, 1)) brightness(1.3)';
      markerElement.style.zIndex = '10000';
      markerElement.style.transition = 'all 0.2s ease';
    } else {
      markerElement.style.filter = '';
      markerElement.style.zIndex = '';
    }
  }, [isHovered]);

  return (
    <Marker
      ref={markerRef}
      position={[park.lat, park.lng]}
      icon={icon}
    >
      <Popup className="bp-popup" maxWidth={700} closeButton={false}>
        <div className="bp-popup-content">
          {/* Header */}
          <div className="bp-popup-header">
            <div className="bp-popup-header-top">
              <h3>{park.name}</h3>
              <button
                className="bp-popup-close"
                onClick={() => markerRef.current?.closePopup()}
                aria-label={t('close')}
              >
                <X size={16} />
              </button>
            </div>
            <div className="bp-popup-meta">
              <div className="bp-popup-rating">
                <span>{renderStars(park.rating)}</span>
              </div>
              {park.region && (
                <div className="bp-popup-region">
                  <span><MapPin size={14} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle'}} /></span>
                  <span>{park.region}</span>
                </div>
              )}
              <div className="bp-popup-favorite">
                <FavoriteButton
                  id={park.documentId}
                  slug={park.slug}
                  name={park.name}
                  type={parkType}
                  variant="compact"
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bp-popup-body">
            {/* Blurb */}
            {park.blurb && (
              <div className="bp-popup-blurb">
                {park.blurb}
              </div>
            )}

            {/* Price */}
            {park.price !== undefined && park.price !== null && (
              <div className={`bp-popup-price ${park.price === 0 ? 'free' : 'paid'}`}>
                <div className="bp-popup-price-label">
                  <span>{park.price === 0 ? <Heart size={14} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle'}} /> : <DollarSign size={14} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle'}} />}</span>
                  <span>{park.price === 0 ? 'Bezpłatne' : `od ${park.price} zł/dzień`}</span>
                </div>
                {park.priceInfo && park.price !== 0 && (
                  <div className="bp-popup-price-info">
                    {park.priceInfo}
                  </div>
                )}
              </div>
            )}

            {/* Trails */}
            {park.trails && park.trails.length > 0 && (
              <div className="bp-popup-trails">
                <div className="bp-popup-trails-header">
                  <span className="trail-icon"><Snowflake size={16} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle'}} /></span>
                  <span>Trasy ({park.trails.length})</span>
                </div>
                <div className="bp-popup-trails-list">
                  {park.trails.slice(0, 3).map((trail, idx) => (
                    <>
                      <span key={`${idx}-diff`} className="trail-difficulty">
                        <Circle
                          size={10}
                          fill={trail.difficulty === 'black' ? '#1f2937' :
                                trail.difficulty === 'red' ? '#ef4444' :
                                trail.difficulty === 'blue' ? '#3b82f6' : '#22c55e'}
                          color={trail.difficulty === 'black' ? '#1f2937' :
                                 trail.difficulty === 'red' ? '#ef4444' :
                                 trail.difficulty === 'blue' ? '#3b82f6' : '#22c55e'}
                        />
                      </span>
                      <span key={`${idx}-name`} className="trail-name">
                        {trail.name}
                      </span>
                      <span key={`${idx}-length`} className="trail-length">
                        {trail.length}m
                      </span>
                    </>
                  ))}
                </div>
                {park.trails.length > 3 && (
                  <div className="bp-popup-trails-more">
                    +{park.trails.length - 3} więcej tras
                  </div>
                )}
              </div>
            )}

            {/* Weather Widget */}
            <div className="bp-popup-weather">
              <WeatherWidget
                lat={park.lat}
                lng={park.lng}
                locationName={park.name}
                compact={true}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bp-popup-footer">
            {/* Primary Button - Detail */}
            <a
              href={`/${parkType}/${park.slug}`}
              className="popup-btn popup-btn-primary"
            >
              <span className="btn-icon"><FileText size={16} /></span>
              <span>Zobacz szczegóły</span>
            </a>

            {/* Secondary Buttons Row */}
            {park.url && (
              <a
                href={park.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="popup-btn popup-btn-secondary website"
              >
                <span className="btn-icon"><Globe size={16} /></span>
                <span>Strona</span>
              </a>
            )}

            {'trailforksUrl' in park && park.trailforksUrl && (
              <a
                href={park.trailforksUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className={`popup-btn popup-btn-secondary trailforks ${!park.url ? 'full-width' : ''}`}
              >
                <span className="btn-icon"><MapIcon size={16} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle'}} /></span>
                <span>Trailforks</span>
              </a>
            )}
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

/**
 * MapView Component
 * @param {MapViewProps} props - Component props
 * @param {Array} props.parks - Array of parks to display on map
 * @param {[number, number]} props.center - Map center coordinates [lat, lng]
 * @param {number} props.zoom - Initial zoom level
 * @param {'bikepark' | 'snowpark'} props.type - Type of parks being displayed
 * @returns {JSX.Element} Map component with markers and popups
 */
export default function MapView({ parks, center = [51.9194, 19.1451], zoom = 6, type = 'bikepark', selectedPark = null, hoveredPark = null }: MapViewProps) {
  console.log('[MapView] Rendering with parks:', parks);
  console.log('[MapView] Parks count:', parks.length);

  const [mapKey, setMapKey] = useState(0);
  const [mapCenter, setMapCenter] = useState<[number, number]>(center);
  const [mapZoom, setMapZoom] = useState(zoom);

  // Force re-render on HMR to prevent Leaflet errors
  useEffect(() => {
    setMapKey(prev => prev + 1);
  }, []);

  // Handle selected park - center map and zoom in
  useEffect(() => {
    if (selectedPark && selectedPark.lat && selectedPark.lng) {
      setMapCenter([selectedPark.lat, selectedPark.lng]);
      setMapZoom(13); // Zoom in to show the selected park
    }
  }, [selectedPark]);

  const renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  return (
    <div className="bp-map">
      <MapContainer
        key={mapKey}
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        attributionControl={true}
        zoomControl={false}
      >
        <MapController center={mapCenter} zoom={mapZoom} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {type === 'pumptrack' || type === 'snowpark' ? (
          <MarkerClusterGroup
            chunkedLoading
            maxClusterRadius={80}
            spiderfyOnMaxZoom={true}
            showCoverageOnHover={false}
            zoomToBoundsOnClick={true}
            disableClusteringAtZoom={15}
            iconCreateFunction={(cluster) => createClusterCustomIcon(cluster, type)}
          >
            {parks.map((park) => (
              <ParkMarker
                key={park.documentId}
                park={park}
                isSelected={selectedPark?.documentId === park.documentId}
                isHovered={hoveredPark?.documentId === park.documentId}
                renderStars={renderStars}
                parkType={type}
              />
            ))}
          </MarkerClusterGroup>
        ) : (
          parks.map((park) => (
            <ParkMarker
              key={park.documentId}
              park={park}
              isSelected={selectedPark?.documentId === park.documentId}
              isHovered={hoveredPark?.documentId === park.documentId}
              renderStars={renderStars}
              parkType={type}
            />
          ))
        )}
      </MapContainer>
    </div>
  );
}
